import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, StickyNote, Gavel, XCircle, CheckCircle2, MessageSquare, Trash2, AlertTriangle, ExternalLink } from "lucide-react";
import { disputesApi, Stars, SourceBadge, DisputeBadge, fmtDate, fmtDT, btnCls } from "./shared";
import { NewDisputeForm, EventForm, ResponseForm } from "./DisputeForms";

const EV_ICON = { call: Phone, email: Mail, note: StickyNote };

function buildTimeline(d) {
  const items = [{ at: d.review.review_ts || d.review.review_date, kind: "review" }];
  d.disputes.forEach(x => {
    items.push({ at: x.submitted_at, kind: "dispute", x });
    if (x.responded_at && x.status !== "pending") items.push({ at: x.responded_at, kind: "response", x });
  });
  d.events.forEach(e => items.push({ at: e.happened_at, kind: "event", e }));
  return items.sort((a, b) => new Date(a.at) - new Date(b.at));
}

function TimelineItem({ it, onDeleteEvent, onDeleteDispute }) {
  const dot = { review: "bg-slate-400", dispute: "bg-amber-500", response: it.x?.status === "removed" ? "bg-emerald-500" : "bg-rose-500", event: "bg-indigo-500" }[it.kind];
  let head, body;
  if (it.kind === "review") { head = "YORUM GELDİ"; body = null; }
  if (it.kind === "dispute") {
    head = <>{it.x.attempt_no}. İTİRAZ · {it.x.submitted_by}{it.x.reason ? ` · ${it.x.reason}` : ""}</>;
    body = <div className="mt-1 text-xs bg-slate-800/60 border-l-2 border-slate-600 px-2.5 py-1.5 rounded-r whitespace-pre-line text-slate-300">{it.x.defense_text}</div>;
  }
  if (it.kind === "response") {
    head = <>PLATFORM CEVABI · {it.x.attempt_no}. itiraz {it.x.status === "removed" ? "· KALDIRILDI" : "· REDDEDİLDİ"}</>;
    body = it.x.response_text && <div className="mt-1 text-xs text-slate-300 whitespace-pre-line">"{it.x.response_text}"</div>;
  }
  if (it.kind === "event") {
    const Icon = EV_ICON[it.e.kind] || MessageSquare;
    head = <><Icon className="inline w-3 h-3 mr-1" />{it.e.kind === "note" ? "NOT" : "GÖRÜŞME"}{it.e.channel ? ` · ${it.e.channel}` : ""}{it.e.contact ? ` · ${it.e.contact}` : ""} · {it.e.created_by}</>;
    body = <div className="mt-1 text-xs text-slate-300 whitespace-pre-line">{it.e.body}</div>;
  }
  return (
    <div className="relative pl-6 pb-4 group" data-testid={`timeline-${it.kind}`}>
      <span className={`absolute left-[3px] top-1 w-2.5 h-2.5 rounded-full ${dot}`} />
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] font-mono text-slate-500">{fmtDT(it.at)} · {head}</div>
        {it.kind === "event" && <button onClick={() => onDeleteEvent(it.e.id)} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-rose-400" data-testid={`event-delete-${it.e.id}`}><Trash2 className="w-3 h-3" /></button>}
        {it.kind === "dispute" && <button onClick={() => onDeleteDispute(it.x.id)} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-rose-400" data-testid={`dispute-delete-${it.x.id}`}><Trash2 className="w-3 h-3" /></button>}
      </div>
      {body}
    </div>
  );
}

export default function DisputePanel({ reviewId, onChanged }) {
  const [d, setD] = useState(null);
  const [mode, setMode] = useState(null);
  const load = useCallback(() => disputesApi.detail(reviewId).then(setD).catch(() => toast.error("Yüklenemedi")), [reviewId]);
  useEffect(() => { setD(null); setMode(null); load(); }, [load]);
  if (!d) return <div className="p-6 text-sm text-slate-500" data-testid="dispute-panel-loading">Yükleniyor…</div>;

  const r = d.review;
  const last = d.disputes[d.disputes.length - 1];
  const done = () => { setMode(null); load(); onChanged && onChanged(); };
  const delEvent = async (id) => { if (window.confirm("Kayıt silinsin mi?")) { await disputesApi.deleteEvent(id); done(); } };
  const delDispute = async (id) => { if (window.confirm("Bu itiraz kaydı silinsin mi?")) { await disputesApi.deleteDispute(id); done(); } };
  const toggleRemoved = async () => {
    const removed = r.dispute_status !== "removed";
    if (!window.confirm(removed ? "Yorum platformdan kaldırıldı olarak işaretlensin mi?" : "Kaldırıldı işareti geri alınsın mı?")) return;
    await disputesApi.setRemoved(r.id, removed); toast.success(removed ? "Kaldırıldı olarak işaretlendi" : "Geri alındı"); done();
  };

  return (
    <div className="flex flex-col" data-testid="dispute-panel">
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2"><SourceBadge source={r.source} /><b className="text-white">{r.reviewer_name || "(isimsiz)"}</b><Stars value={r.rating} /></div>
          <DisputeBadge status={r.dispute_status} count={r.dispute_count} testId="dispute-panel-status" />
        </div>
        <div className="text-xs text-slate-500 mt-1">{fmtDate(r.review_date)}{r.product_name ? ` · ${r.product_name}` : ""}{r.confirmation_code ? ` · ${r.confirmation_code}` : ""}{r.garson ? ` · Garson: ${r.garson}` : ""}
          {r.source_url && <a href={r.source_url} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-0.5 text-blue-500 hover:underline"><ExternalLink className="w-3 h-3" />kaynak</a>}</div>
        {r.body && <p className="text-xs text-slate-300 mt-2 line-clamp-3">"{r.body}"</p>}
        {r.maybe_removed && <div className="mt-2 text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-sky-100 text-sky-800 border border-sky-300" data-testid="maybe-removed-note"><AlertTriangle className="w-3 h-3" /> Son yorum senkronlarında bu yorum artık gelmiyor — kaldırılmış olabilir, kontrol edin</div>}
      </div>
      <div className="p-4 max-h-[46vh] overflow-y-auto relative">
        <div className="absolute left-[23px] top-5 bottom-5 w-px bg-slate-700" />
        {buildTimeline(d).map((it, i) => <TimelineItem key={i} it={it} onDeleteEvent={delEvent} onDeleteDispute={delDispute} />)}
      </div>
      <div className="p-4 border-t border-slate-700 space-y-3">
        {mode === "dispute" && <NewDisputeForm reviewId={r.id} attemptNo={(last?.attempt_no || 0) + 1} onDone={done} onCancel={() => setMode(null)} />}
        {mode === "event" && <EventForm reviewId={r.id} onDone={done} onCancel={() => setMode(null)} />}
        {mode === "response" && last && <ResponseForm dispute={last} onDone={done} onCancel={() => setMode(null)} />}
        {!mode && (
          <div className="flex flex-wrap gap-2">
            {r.dispute_status !== "pending" && r.dispute_status !== "removed" && <button onClick={() => setMode("dispute")} className={`${btnCls} bg-blue-600 hover:bg-blue-500 text-white py-1.5`} data-testid="dispute-new-btn"><Gavel className="w-4 h-4" /> {last ? `${last.attempt_no + 1}. itirazı yap` : "İtiraz et"}</button>}
            {r.dispute_status === "pending" && <button onClick={() => setMode("response")} className={`${btnCls} bg-amber-600 hover:bg-amber-500 text-white py-1.5`} data-testid="dispute-response-btn"><XCircle className="w-4 h-4" /> Platform cevabı gir</button>}
            <button onClick={() => setMode("event")} className={`${btnCls} bg-slate-700 hover:bg-slate-600 text-slate-200 py-1.5`} data-testid="dispute-event-btn"><Phone className="w-4 h-4" /> Görüşme / not ekle</button>
            <button onClick={toggleRemoved} className={`${btnCls} ${r.dispute_status === "removed" ? "bg-slate-700 hover:bg-slate-600 text-slate-200" : "bg-emerald-600 hover:bg-emerald-500 text-white"} py-1.5`} data-testid="dispute-removed-btn"><CheckCircle2 className="w-4 h-4" /> {r.dispute_status === "removed" ? "Kaldırıldı işaretini geri al" : "Kaldırıldı olarak işaretle"}</button>
          </div>
        )}
      </div>
    </div>
  );
}
